import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { MobileEventSelectorBreakawayDallas } from "@/components/rider/MobileEventSelectorBreakawayDallas";
import { DesktopEventSelectorBreakawayDallas } from "@/components/rider/desktop/DesktopEventSelectorBreakawayDallas";
import { LoadingEvents } from "@/components/rider/LoadingEvents";
import { CommunityEvent } from "@/types/rider";
import { getEventById } from "@/services/riderService";

export interface BreakawayDallasVehicleOption {
  id: string;
  name: string;
  capacity: number;
  price: number;
  isRoundTrip: boolean;
  durationHours: number;
  icon: string;
}

export type BreakawayDallasDaySelection = 'friday' | 'saturday';

// Default vehicle options - will be overwritten by API data
const defaultVehicleOptions: BreakawayDallasVehicleOption[] = [
  { id: 'shuttle_single_day', name: 'Breakaway Single Day Shuttle Pass', capacity: 1, price: 30, isRoundTrip: true, durationHours: 1, icon: '🚐' },
  { id: 'shuttle_2_day', name: 'Breakaway 2-Day Shuttle Pass', capacity: 1, price: 50, isRoundTrip: true, durationHours: 1, icon: '🚐' },
  { id: 'party_bus_one_way', name: 'One Way Party Bus', capacity: 25, price: 800, isRoundTrip: false, durationHours: 2, icon: '🚌' },
  { id: 'party_bus_round_trip', name: 'Round Trip Party Bus', capacity: 25, price: 1400, isRoundTrip: true, durationHours: 5, icon: '🚌' },
  { id: 'party_bus_all_day', name: 'All Day Party Bus', capacity: 25, price: 2000, isRoundTrip: true, durationHours: 8, icon: '🚌' },
  { id: 'vip_experience', name: 'VIP Party Bus Experience', capacity: 20, price: 2500, isRoundTrip: true, durationHours: 10, icon: '🎉' }
];

const RiderEventSelectBreakawayDallas = () => {
  const [event, setEvent] = useState<CommunityEvent | null>(null);
  const [vehicleOptions, setVehicleOptions] = useState<BreakawayDallasVehicleOption[]>(defaultVehicleOptions);
  const [selectedDay, setSelectedDay] = useState<BreakawayDallasDaySelection>('friday');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [eventSelected, setEventSelected] = useState(false);
  const navigate = useNavigate();
  const [eventImage, setEventImage] = useState<string>("/Default_Party_Bus.jpeg");

  // Breakaway Dallas event ID from the database
  const BREAKAWAY_DALLAS_EVENT_ID = "07ba29a6-c059-446d-a8b2-36a63e71a330";

  // Day to date mapping for Breakaway Dallas - Friday and Saturday only
  const dayDates: Record<BreakawayDallasDaySelection, string> = {
    friday: 'Friday, TBD',
    saturday: 'Saturday, TBD'
  };

  // Helper function to convert relative image paths to absolute URLs
  const getAbsoluteImageUrl = (imagePath: string) => {
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    return `${window.location.origin}${imagePath}`;
  };

  useEffect(() => {
    const loadEventData = async () => {
      try {
        console.log("Loading Breakaway Dallas event with ID:", BREAKAWAY_DALLAS_EVENT_ID);
        const eventData = await getEventById(BREAKAWAY_DALLAS_EVENT_ID);
        
        if (eventData) {
          console.log("Received Breakaway Dallas event data:", eventData);
          console.log("Bus pricing info:", eventData.bus_pricing_info);
          
          // Set single event with custom multi-day date display
          setEvent({
            ...eventData,
            name: eventData.name || "Breakaway Dallas",
            location: eventData.location || "Dallas, TX",
            region: "Dallas",
            date: eventData.date || "Friday & Saturday"
          });
          
          // Transform bus_pricing_info into vehicle options if available
          if (eventData.bus_pricing_info) {
            const pricingInfo = eventData.bus_pricing_info;
            console.log("Processing bus_pricing_info:", pricingInfo);
            
            const transformedOptions: BreakawayDallasVehicleOption[] = Object.entries(pricingInfo).map(([key, value]: [string, any]) => ({
              id: key,
              name: value.user_friendly_name || key,
              capacity: value.passenger_count || 1,
              price: value.rider_price || 0,
              isRoundTrip: value.is_round_trip || false,
              durationHours: value.duration_hours || 1,
              icon: key.includes('shuttle') ? '🚐' : (key.includes('vip') ? '🎉' : '🚌')
            }));
            
            console.log("Transformed vehicle options:", transformedOptions);
            
            if (transformedOptions.length > 0) {
              setVehicleOptions(transformedOptions);
            }
          }
          
          // Update day dates if event has date info
          if (eventData.starts_at) {
            const startDate = new Date(eventData.starts_at);
            // This would need more complex logic to extract Friday/Saturday dates
            // For now, keep the placeholder dates
          }
          
          // Set the event image for meta tags
          if (eventData.image) {
            setEventImage(eventData.image);
          }
        } else {
          // Fallback event data
          console.log("No event data received, using fallback");
          setEvent({
            id: BREAKAWAY_DALLAS_EVENT_ID,
            name: "Breakaway Dallas",
            location: "Dallas, TX",
            region: "Dallas",
            image: "/Default_Party_Bus.jpeg",
            date: "Friday & Saturday",
            startTime: "TBD",
            price: 30
          });
        }
      } catch (error) {
        console.error("Failed to load Breakaway Dallas event:", error);
        // Fallback event data
        setEvent({
          id: BREAKAWAY_DALLAS_EVENT_ID,
          name: "Breakaway Dallas",
          location: "Dallas, TX",
          region: "Dallas",
          image: "/Default_Party_Bus.jpeg",
          date: "Friday & Saturday",
          startTime: "TBD",
          price: 30
        });
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, []);

  const handleEventSelect = () => {
    setEventSelected(true);
  };

  const handleBackToEvent = () => {
    setEventSelected(false);
    setSelectedVehicleId(null);
  };

  const handleDaySelect = (day: BreakawayDallasDaySelection) => {
    setSelectedDay(day);
  };

  const handleVehicleSelection = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
  };

  const handleContinue = () => {
    if (selectedVehicleId && event) {
      const selectedVehicle = vehicleOptions.find(v => v.id === selectedVehicleId);
      if (!selectedVehicle) return;

      const venueAddress = event.location || "Breakaway Dallas, Dallas, TX";
      
      // Detect if this is a shuttle ticket
      const isShuttle = selectedVehicle.name.toLowerCase().includes('shuttle');
      
      const params = new URLSearchParams({ 
        eventId: BREAKAWAY_DALLAS_EVENT_ID,
        capacity: selectedVehicle.capacity.toString(),
        isRoundTrip: selectedVehicle.isRoundTrip.toString(),
        destination: venueAddress,
        vehicleType: selectedVehicle.name,
        pricingKey: selectedVehicle.id,
        selectedDay: selectedDay,
        price: selectedVehicle.price.toString(),
        durationHours: selectedVehicle.durationHours.toString()
      });
      
      // Add shuttle flag if applicable
      if (isShuttle) {
        params.append('isShuttle', 'true');
      }
      
      // Create notes with vehicle information
      const notes = `${selectedVehicle.name} - ${isShuttle ? 'Shuttle' : `Capacity ${selectedVehicle.capacity}`} - ${selectedVehicle.isRoundTrip ? 'Round Trip' : 'One Way'} - ${selectedVehicle.durationHours}hr - ${dayDates[selectedDay]}`;
      params.append('notes', notes);
      params.append('returnPath', '/breakaway-dallas');
      
      // Only add time presets for non-shuttle flows
      if (!isShuttle) {
        const timePresets = [
          { label: "Early Entry", time: "15:00", description: "3:00 PM pickup" },
          { label: "Main Event", time: "18:00", description: "6:00 PM pickup" },
          { label: "Late Night", time: "23:00", description: "11:00 PM pickup" }
        ];
        params.append('timePresets', JSON.stringify(timePresets));
      }
      
      navigate(`/rider-quote?${params.toString()}`);
    }
  };

  const handleManualEntry = () => {
    navigate('/');
  };

  // Available days for Breakaway Dallas (Friday and Saturday only)
  const availableDays: BreakawayDallasDaySelection[] = ['friday', 'saturday'];

  return (
    <RiderLayout>
      <Helmet>
        <title>Breakaway Dallas | Disco Party Bus</title>
        <meta name="description" content="Book your party bus or shuttle for Breakaway Dallas - The ultimate music festival experience" />
        <meta property="og:title" content="Breakaway Dallas | Disco Party Bus" />
        <meta property="og:description" content="Book your party bus or shuttle for Breakaway Dallas - The ultimate music festival experience" />
        <meta property="og:image" content={getAbsoluteImageUrl(eventImage)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={`${window.location.origin}/breakaway-dallas`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={getAbsoluteImageUrl(eventImage)} />
      </Helmet>
      <div className="min-h-screen bg-white transition-colors duration-300">
        {loading ? (
          <LoadingEvents />
        ) : event ? (
          <>
            <MobileEventSelectorBreakawayDallas
              event={event}
              eventSelected={eventSelected}
              selectedDay={selectedDay}
              selectedVehicleId={selectedVehicleId}
              vehicleOptions={vehicleOptions}
              dayDates={dayDates}
              availableDays={availableDays}
              onEventSelect={handleEventSelect}
              onBackToEvent={handleBackToEvent}
              onDaySelect={handleDaySelect}
              onVehicleSelect={handleVehicleSelection}
              onContinue={handleContinue}
              onManualEntry={handleManualEntry}
            />

            <DesktopEventSelectorBreakawayDallas
              event={event}
              eventSelected={eventSelected}
              selectedDay={selectedDay}
              selectedVehicleId={selectedVehicleId}
              vehicleOptions={vehicleOptions}
              dayDates={dayDates}
              availableDays={availableDays}
              onEventSelect={handleEventSelect}
              onBackToEvent={handleBackToEvent}
              onDaySelect={handleDaySelect}
              onVehicleSelect={handleVehicleSelection}
              onContinue={handleContinue}
              onManualEntry={handleManualEntry}
            />
          </>
        ) : null}
      </div>
    </RiderLayout>
  );
};

export default RiderEventSelectBreakawayDallas;
